import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Plus, Users, Send, Search, ArrowLeft, UsersRound, User as UserIcon, Check, CheckCheck, Loader2, Reply, Forward, Trash2, Smile, MoreVertical, LogOut, X, Pin } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { normalize as _nz } from "../lib/search";

const EMOJIS = ["😀","😁","😂","🤣","😊","😍","😉","😎","🥰","😘","👍","👎","🙏","👏","💪","🎉","🔥","✅","❌","⚠️","❤️","💯","🙌","👌","🤝","😅","😢","😭","😡","🤔","🙄","😴","🥳","😱","🤯","💰","📌","📎","📷","⏰","📅","🚀","⭐","✨","💡","🎯","📞","💬"];

const MsgActions = ({ m, canDel, onReply, onForward, onDelete, onPin }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="opacity-40 group-hover:opacity-100 transition-opacity text-muted-foreground p-1 shrink-0" data-testid={`msg-menu-${m.id}`}><MoreVertical className="h-4 w-4" /></button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={onPin} data-testid={`pin-${m.id}`}><Pin className="h-4 w-4 mr-2" />{m.pinned ? "Quitar fijado" : "Fijar"}</DropdownMenuItem>
      <DropdownMenuItem onClick={onReply} data-testid={`reply-${m.id}`}><Reply className="h-4 w-4 mr-2" />Responder</DropdownMenuItem>
      <DropdownMenuItem onClick={onForward} data-testid={`forward-${m.id}`}><Forward className="h-4 w-4 mr-2" />Reenviar</DropdownMenuItem>
      {canDel && <DropdownMenuItem className="text-destructive" onClick={onDelete} data-testid={`delete-msg-${m.id}`}><Trash2 className="h-4 w-4 mr-2" />Eliminar</DropdownMenuItem>}
    </DropdownMenuContent>
  </DropdownMenu>
);

const timeStr = (iso) => { try { return new Date(iso).toLocaleTimeString("es-GT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const sameDay = (a, b) => { try { return new Date(a).toDateString() === new Date(b).toDateString(); } catch { return false; } };
const dateLabel = (iso) => {
  try {
    const d = new Date(iso), today = new Date(), yest = new Date(); yest.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Hoy";
    if (d.toDateString() === yest.toDateString()) return "Ayer";
    return d.toLocaleDateString("es-GT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return ""; }
};
const Ticks = ({ status }) => {
  if (status === "read") return <CheckCheck className="h-3.5 w-3.5 text-sky-300" data-testid="tick-read" />;
  if (status === "delivered") return <CheckCheck className="h-3.5 w-3.5" data-testid="tick-delivered" />;
  return <Check className="h-3.5 w-3.5" data-testid="tick-sent" />;
};
const Avatar = ({ text, group, size = "h-10 w-10" }) => (
  <div className={`${size} rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${group ? "bg-primary/15 text-primary" : "bg-secondary text-secondary-foreground"}`}>
    {group ? <UsersRound className="h-4 w-4" /> : text}
  </div>
);

export default function Chat() {
  const { user } = useAuth();
  const location = useLocation();
  const [convs, setConvs] = useState([]);
  const [canGroups, setCanGroups] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [mode, setMode] = useState("direct");
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [msgSearch, setMsgSearch] = useState("");
  const [info, setInfo] = useState(null);
  const endRef = useRef(null);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  const loadConvs = useCallback(() => {
    api.get("/chat/conversations").then((r) => { setConvs(r.data.items); setCanGroups(r.data.can_manage_groups); }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const loadMessages = useCallback((cid) => {
    if (!cid) return;
    api.get(`/chat/conversations/${cid}/messages`).then((r) => setActive(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => {
    const t = setInterval(() => { loadConvs(); if (activeIdRef.current) loadMessages(activeIdRef.current); }, 4000);
    return () => clearInterval(t);
  }, [loadConvs, loadMessages]);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active?.items?.length, activeId]);

  // Abrir conversación desde ?c=<id> (clic en notificación push)
  useEffect(() => {
    const p = new URLSearchParams(location.search).get("c");
    if (p) setActiveId(p);
  }, [location.search]);

  const openPicker = (m) => {
    setMode(m); setSelected([]); setGroupName(""); setSearch("");
    api.get("/chat/users").then((r) => setPeople(r.data)).catch(() => {});
    setNewOpen(true);
  };
  const startDirect = async (uid) => {
    try { const r = await api.post("/chat/direct", { user_id: uid }); setNewOpen(false); setActiveId(r.data.id); loadConvs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createGroup = async () => {
    try { const r = await api.post("/chat/groups", { name: groupName, member_ids: selected }); toast.success("Grupo creado"); setNewOpen(false); setActiveId(r.data.id); loadConvs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const send = async () => {
    const t = text.trim(); if (!t || sending) return;
    setSending(true); setText(""); setEmojiOpen(false);
    const rid = replyTo?.id || null; setReplyTo(null);
    try {
      const r = await api.post(`/chat/conversations/${activeId}/messages`, { text: t, reply_to_id: rid });
      setActive((a) => a ? { ...a, items: [...a.items, r.data] } : a);
      loadConvs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setText(t); }
    finally { setSending(false); }
  };
  const deleteMessage = async (mid) => {
    try { await api.delete(`/chat/conversations/${activeId}/messages/${mid}`); loadMessages(activeId); loadConvs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openInfo = async (m) => {
    setInfo({ msg: m, loading: true });
    try { const r = await api.get(`/chat/conversations/${activeId}/messages/${m.id}/info`); setInfo({ msg: m, loading: false, ...r.data }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setInfo(null); }
  };
  const deleteConversation = async () => {
    if (!activeId) return;
    try { await api.delete(`/chat/conversations/${activeId}`); toast.success("Chat eliminado"); setActiveId(null); setActive(null); loadConvs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doForward = async (targetId) => {
    try {
      await api.post(`/chat/conversations/${targetId}/messages`, { text: forwardMsg.text, forwarded: true });
      toast.success("Mensaje reenviado"); setForwardMsg(null);
      if (targetId === activeId) loadMessages(activeId);
      loadConvs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const filtered = people.filter((p) => _nz(p.name).includes(_nz(search)));
  const togglePin = async (mid) => { try { await api.post(`/chat/conversations/${activeId}/messages/${mid}/pin`); loadMessages(activeId); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const _norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const mq = _norm(msgSearch.trim());
  const shownMsgs = active ? (mq ? active.items.filter((m) => !m.deleted && _norm(m.text).includes(mq)) : active.items) : [];

  return (
    <div className="h-[calc(100vh-9rem)] flex border border-border rounded-sm overflow-hidden bg-card" data-testid="chat-page">
      {/* Lista de conversaciones */}
      <div className={`w-full sm:w-80 border-r border-border flex flex-col ${activeId ? "hidden sm:flex" : "flex"}`}>
        <div className="p-3 border-b border-border flex items-center justify-between gap-2">
          <h2 className="font-heading font-bold flex items-center gap-2"><MessageCircle className="h-5 w-5 text-primary" />Mensajes</h2>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-8 rounded-sm text-xs" onClick={() => openPicker("direct")} data-testid="new-direct-btn"><UserIcon className="h-3.5 w-3.5 mr-1" />Nuevo</Button>
            {canGroups && <Button size="sm" variant="outline" className="h-8 rounded-sm text-xs" onClick={() => openPicker("group")} data-testid="new-group-btn"><Users className="h-3.5 w-3.5 mr-1" />Grupo</Button>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
          {loading && <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
          {!loading && convs.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Sin conversaciones. Toca "Nuevo" para empezar a chatear.</p>}
          {convs.map((c) => (
            <button key={c.id} onClick={() => setActiveId(c.id)} data-testid={`conv-${c.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-border/50 hover:bg-muted/50 transition-colors ${activeId === c.id ? "bg-muted" : ""}`}>
              <Avatar text={c.initials} group={c.type === "group"} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm truncate">{c.title}</p>
                  <span className="text-[10px] text-muted-foreground shrink-0">{c.last_at ? timeStr(c.last_at) : ""}</span>
                </div>
                <p className="text-xs text-muted-foreground truncate">{c.last_message || (c.type === "group" ? `${c.members_count} miembros` : "Nueva conversación")}</p>
              </div>
              {c.unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid={`unread-${c.id}`}>{c.unread}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Panel de conversación */}
      <div className={`flex-1 flex-col ${activeId ? "flex" : "hidden sm:flex"}`}>
        {!activeId && <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2"><MessageCircle className="h-12 w-12 opacity-30" /><p className="text-sm">Selecciona una conversación</p></div>}
        {activeId && active && (
          <>
            <div className="p-3 border-b border-border flex items-center gap-3">
              <button className="sm:hidden" onClick={() => setActiveId(null)} data-testid="chat-back-btn"><ArrowLeft className="h-5 w-5" /></button>
              <Avatar text={(active.title || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()} group={active.type === "group"} size="h-9 w-9" />
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold truncate">{active.title}</p>
                {active.type === "group" && <p className="text-[11px] text-muted-foreground truncate">{active.members.map((m) => m.name).join(", ")}</p>}
              </div>
              <Button size="icon" variant="ghost" className="rounded-full shrink-0" onClick={() => setSearchOpen((v) => !v)} data-testid="conv-search-btn"><Search className="h-5 w-5" /></Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="rounded-full shrink-0" data-testid="conv-menu-btn"><MoreVertical className="h-5 w-5" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {active.type === "group" && !(user.is_admin || active.created_by === user.id)
                    ? <DropdownMenuItem onClick={deleteConversation} data-testid="leave-group"><LogOut className="h-4 w-4 mr-2" />Salir del grupo</DropdownMenuItem>
                    : <DropdownMenuItem className="text-destructive" onClick={deleteConversation} data-testid="delete-conv"><Trash2 className="h-4 w-4 mr-2" />{active.type === "group" ? "Eliminar grupo" : "Eliminar chat"}</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {searchOpen && (
              <div className="p-2 border-b border-border flex items-center gap-2" data-testid="search-bar">
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input value={msgSearch} onChange={(e) => setMsgSearch(e.target.value)} placeholder="Buscar en esta conversación…" className="rounded-sm h-8" data-testid="msg-search" data-enter-ignore autoFocus />
                <button onClick={() => { setSearchOpen(false); setMsgSearch(""); }} data-testid="close-search"><X className="h-4 w-4" /></button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-muted/20" data-testid="messages-pane">
              {active.pins && active.pins.length > 0 && !mq && (
                <div className="mb-2 space-y-1" data-testid="pinned-bar">
                  {active.pins.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 text-xs bg-primary/5 border border-primary/20 rounded-sm px-2 py-1" data-testid={`pinned-${p.id}`}>
                      <Pin className="h-3.5 w-3.5 text-primary shrink-0" />
                      <div className="flex-1 min-w-0"><span className="font-bold">{p.sender_name}: </span><span className="text-muted-foreground">{p.text}</span></div>
                      <button onClick={() => togglePin(p.id)} data-testid={`unpin-${p.id}`}><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
              {active.items.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Aún no hay mensajes. ¡Escribe el primero!</p>}
              {mq && shownMsgs.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Sin resultados para "{msgSearch}".</p>}
              {shownMsgs.map((m, i) => {
                const mine = m.sender_id === user.id;
                const canDel = mine || user.is_admin || active.created_by === user.id;
                const showDate = i === 0 || !sameDay(shownMsgs[i - 1].created_at, m.created_at);
                return (
                  <React.Fragment key={m.id}>
                    {showDate && <div className="flex justify-center my-2"><span className="text-[10px] font-bold uppercase tracking-wider bg-muted text-muted-foreground px-2.5 py-1 rounded-full" data-testid="date-separator">{dateLabel(m.created_at)}</span></div>}
                    <div className={`group flex items-center gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    {mine && !m.deleted && <MsgActions m={m} canDel={canDel} onReply={() => setReplyTo({ id: m.id, sender_name: m.sender_name, text: m.text })} onForward={() => setForwardMsg(m)} onDelete={() => deleteMessage(m.id)} onPin={() => togglePin(m.id)} />}
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                      {!mine && active.type === "group" && !m.deleted && <p className="text-[11px] font-bold text-primary mb-0.5">{m.sender_name}</p>}
                      {m.forwarded && !m.deleted && <p className="text-[10px] italic opacity-70 mb-0.5 flex items-center gap-1"><Forward className="h-3 w-3" />Reenviado</p>}
                      {m.reply_to && !m.deleted && (
                        <div className={`text-[11px] border-l-2 pl-2 mb-1 ${mine ? "border-primary-foreground/50" : "border-primary"}`}>
                          <p className="font-bold truncate">{m.reply_to.sender_name}</p>
                          <p className="opacity-80 truncate">{m.reply_to.text}</p>
                        </div>
                      )}
                      {m.deleted
                        ? <p className="text-sm italic opacity-60">🚫 Mensaje eliminado</p>
                        : <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                      <p className={`text-[10px] mt-0.5 text-right flex items-center justify-end gap-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{m.pinned && !m.deleted && <Pin className="h-3 w-3" />}{timeStr(m.created_at)}{mine && !m.deleted && (active.type === "group"
                        ? <button onClick={() => openInfo(m)} data-testid={`ticks-${m.id}`} className="ml-0.5 hover:opacity-100"><Ticks status={m.status} /></button>
                        : <Ticks status={m.status} />)}</p>
                    </div>
                    {!mine && !m.deleted && <MsgActions m={m} canDel={canDel} onReply={() => setReplyTo({ id: m.id, sender_name: m.sender_name, text: m.text })} onForward={() => setForwardMsg(m)} onDelete={() => deleteMessage(m.id)} onPin={() => togglePin(m.id)} />}
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="border-t border-border">
              {replyTo && (
                <div className="flex items-center gap-2 px-3 pt-2 text-xs" data-testid="reply-preview">
                  <div className="flex-1 border-l-2 border-primary pl-2 min-w-0">
                    <p className="font-bold text-primary truncate">Respondiendo a {replyTo.sender_name}</p>
                    <p className="text-muted-foreground truncate">{replyTo.text}</p>
                  </div>
                  <button onClick={() => setReplyTo(null)} data-testid="cancel-reply"><X className="h-4 w-4" /></button>
                </div>
              )}
              <div className="p-3 flex items-center gap-2 relative">
                {emojiOpen && (
                  <div className="absolute bottom-14 left-2 bg-card border border-border rounded-lg p-2 shadow-lg grid grid-cols-8 gap-0.5 z-20 w-72 max-h-48 overflow-y-auto" data-testid="emoji-popover">
                    {EMOJIS.map((e) => <button key={e} type="button" className="text-xl hover:bg-muted rounded p-0.5" onClick={() => setText((t) => t + e)}>{e}</button>)}
                  </div>
                )}
                <Button size="icon" variant="ghost" className="rounded-full shrink-0" onClick={() => setEmojiOpen((v) => !v)} data-testid="emoji-btn"><Smile className="h-5 w-5" /></Button>
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Escribe un mensaje…" className="rounded-full" data-testid="chat-input" data-enter-ignore />
                <Button size="icon" className="rounded-full shrink-0" onClick={send} disabled={sending || !text.trim()} data-testid="send-btn"><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Diálogo Nuevo chat / Grupo */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-sm max-w-md max-h-[85vh] overflow-hidden flex flex-col" data-testid="new-chat-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{mode === "group" ? "Crear grupo" : "Nuevo chat"}</DialogTitle>
            <DialogDescription>{mode === "group" ? "Ponle un nombre y elige a los miembros." : "Elige un colaborador para chatear."}</DialogDescription>
          </DialogHeader>
          {mode === "group" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Nombre del grupo</Label>
              <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Ej. Ventas, Bodega…" className="rounded-sm" data-testid="group-name-input" />
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar colaborador…" className="rounded-sm pl-8" data-testid="people-search" />
          </div>
          <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1" data-testid="people-list">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => mode === "group" ? toggleSel(p.id) : startDirect(p.id)} data-testid={`person-${p.id}`}
                className={`w-full flex items-center gap-3 p-2 rounded-sm text-left hover:bg-muted transition-colors ${selected.includes(p.id) ? "bg-primary/10" : ""}`}>
                <Avatar text={p.initials} size="h-9 w-9" />
                <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{p.name}</p>{p.position && <p className="text-[11px] text-muted-foreground truncate">{p.position}</p>}</div>
                {mode === "group" && <span className={`h-5 w-5 rounded border flex items-center justify-center ${selected.includes(p.id) ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>{selected.includes(p.id) && <Check className="h-3.5 w-3.5" />}</span>}
              </button>
            ))}
            {filtered.length === 0 && <p className="text-center text-sm text-muted-foreground py-6">Sin resultados.</p>}
          </div>
          {mode === "group" && (
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setNewOpen(false)}>Cancelar</Button>
              <Button className="rounded-sm font-bold" onClick={createGroup} disabled={!groupName.trim() || selected.length === 0} data-testid="create-group-btn">Crear grupo ({selected.length})</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo Reenviar */}
      <Dialog open={!!forwardMsg} onOpenChange={(v) => !v && setForwardMsg(null)}>
        <DialogContent className="rounded-sm max-w-sm max-h-[80vh] overflow-hidden flex flex-col" data-testid="forward-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reenviar mensaje</DialogTitle>
            <DialogDescription className="truncate">"{forwardMsg?.text}"</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-1" data-testid="forward-list">
            {convs.map((c) => (
              <button key={c.id} onClick={() => doForward(c.id)} data-testid={`forward-to-${c.id}`} className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-muted text-left">
                <Avatar text={c.initials} group={c.type === "group"} size="h-9 w-9" />
                <p className="font-medium text-sm truncate">{c.title}</p>
              </button>
            ))}
            {convs.length === 0 && <p className="text-center text-sm text-muted-foreground py-6">No tienes conversaciones para reenviar.</p>}
          </div>
        </DialogContent>
      </Dialog>
      {/* Diálogo Información del mensaje (grupos) */}
      <Dialog open={!!info} onOpenChange={(v) => !v && setInfo(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="msg-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Información del mensaje</DialogTitle>
            <DialogDescription className="truncate">"{info?.msg?.text}"</DialogDescription>
          </DialogHeader>
          {info?.loading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-sky-500 flex items-center gap-1.5 mb-2"><CheckCheck className="h-4 w-4" /> Leído por ({info?.read_by?.length || 0})</p>
                {(info?.read_by || []).length === 0 ? <p className="text-sm text-muted-foreground">Nadie todavía.</p>
                  : <div className="space-y-1" data-testid="read-by-list">{info.read_by.map((n) => <p key={n} className="text-sm">{n}</p>)}</div>}
              </div>
              {(info?.delivered_to || []).length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2"><CheckCheck className="h-4 w-4" /> Entregado a ({info.delivered_to.length})</p>
                  <div className="space-y-1">{info.delivered_to.map((n) => <p key={n} className="text-sm text-muted-foreground">{n}</p>)}</div>
                </div>
              )}
              {(info?.pending || []).length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2"><Check className="h-4 w-4" /> Pendiente ({info.pending.length})</p>
                  <div className="space-y-1">{info.pending.map((n) => <p key={n} className="text-sm text-muted-foreground">{n}</p>)}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}